/**
 * Framework-agnostic component URL resolver.
 *
 * Every framework's mount engine needs to turn an SDX
 * component ID (`theme:component-name`) into a URL the
 * browser can `import()`. The map is supplied by the
 * framework's `pageAttachments` hook from
 * `ComponentBrowserUrlMap`, which reads the Vite manifest
 * to find each entry's content-hashed filename.
 *
 * Loader coordination: SDX has three independent module loaders
 * on the page - Drupal's library/asset system (which emits
 * `<script type="module" src="...">` for any entry chunk a
 * library declares), the mount coordinator's dynamic `import()`
 * (this resolver), and the active page renderer's
 * `<link rel="modulepreload">` tags (which preload entries +
 * closure ahead of mount). Vite content-hashes entry filenames
 * so all three loaders resolve to identical URL strings per
 * file - the browser's module map deduplicates by exact URL
 * match and collapses every fetch into a single network request.
 *
 * Cache-busting: the hashed filename IS the cache-bust. Drupal's
 * per-build asset query string is not appended (the library
 * declarations use `type: external` for the same reason); the
 * filename changes whenever the content does.
 */

import type { FrameworkSettings } from './types';

export function resolveComponentPath(
  componentId: string,
  componentMap: Record<string, string>,
  devServer: string | null,
  settings: FrameworkSettings,
  logPrefix: string,
): string | null {
  const mapped = componentMap[componentId];
  if (mapped) {
    if (devServer && mapped.startsWith('/')) {
      return `${devServer}${mapped}`;
    }
    return mapped;
  }

  // Convention fallback for components missing from the manifest
  // map. With hashed entries this filename will not exist on
  // disk - the resolver returns it anyway so the browser surfaces
  // a 404 (the right signal that the build is stale or the
  // component was added after the last `drush sdx:build`). Dev
  // mode bypasses this branch entirely; the dev manifest map
  // covers every component.
  const [extension, name] = componentId.split(':');
  if (!extension || !name) {
    return null;
  }
  if (!settings.dist_path) {
    console.error(`${logPrefix} Missing dist_path in settings`);
    return null;
  }
  const distUrl = `${settings.dist_path}/${extension}_${name}.js`;
  return devServer ? `${devServer}${distUrl}` : distUrl;
}

/**
 * Builds the navigation-time resolver a framework hands to the SPA
 * engine (`registerNavigationComponentResolver`).
 *
 * Navigation has no mount context (no componentMap, no devServer at the
 * call site), so the resolver always reads the framework's drupalSettings
 * namespace. Every framework module used to carry this function
 * verbatim; it only differs in the settings reader and the log prefix.
 */
export function createNavigationComponentResolver(
  readSettings: () => FrameworkSettings,
  logPrefix: string,
): (componentId: string) => string | null {
  return (componentId: string) => {
    const settings = readSettings();
    return resolveComponentPath(componentId, settings.components ?? {}, settings.dev_server ?? null, settings, logPrefix);
  };
}
